import React, { useState } from 'react'
import parse from 'html-react-parser'
import { styled } from 'goober'
import { generateBodyPreview, regexList } from '../../../utils/helpers'
import { useTheme } from '../../../hooks/useTheme'
import { NotificationIcon } from './NotificationIcon'
import { NotificationMedia } from './NotificationMedia'
import { Button } from '../../../components'

// Local styled components that don't depend on context
const InAppMessageContainer = styled('section')(({ theme }) => ({
  padding: '0.3rem',
  cursor: 'pointer',
  '&:hover': { translate: '0 -2px' },
  transition: '0.3s translate ease-in-out'
}))

const CustomFlexBox = styled('div')(
  ({ alignItems, justifyContent = '', gap = '', flexDirection = 'row' }) => ({
    display: 'flex',
    flexDirection,
    alignItems,
    justifyContent,
    gap
  })
)

const NotificationContentContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  color: theme.palette?.text?.primary || '#000'
}))

const NotificationContentTypography = styled('p')(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 'bold',
  wordWrap: 'break-word',
  paddingRight: '5px',
  margin: 0,
  color: theme.palette?.text?.primary || '#000'
}))

const ReadMoreTypographyStyled = styled('p')(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 300,
  marginTop: '5px',
  color: theme.palette?.text?.primary || '#000'
}))

const StyledReadMoreButton = styled('button')(({ theme }) => ({
  all: 'unset',
  fontSize: '12px',
  textDecoration: 'none',
  color: theme.palette?.primary?.main || '#1976d2',
  cursor: 'pointer',
  display: 'inline-block',
  marginTop: '5px'
}))

const NotificationContentActionButtonsContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  marginTop: '10px'
})

// Toast-safe ReadMoreTypography component
const ReadMoreTypography = ({ renderBody, theme }) => {
  const [expanded, setExpanded] = useState(false)

  const MAX_CHARS = 150
  const isLong = renderBody?.length > MAX_CHARS

  const displayText = expanded ? renderBody : renderBody?.slice(0, MAX_CHARS)
  const toggleExpanded = (e) => {
    e.stopPropagation()
    setExpanded(!expanded)
  }

  return (
    <React.Fragment>
      <ReadMoreTypographyStyled theme={theme}>
        {parse(displayText)}
        {isLong && !expanded && '...'}
      </ReadMoreTypographyStyled>

      {isLong && (
        <StyledReadMoreButton onClick={toggleExpanded} theme={theme}>
          {expanded ? 'Show less' : 'Read more'}
        </StyledReadMoreButton>
      )}
    </React.Fragment>
  )
}

const ToastNotificationContentInner = ({ body, title, theme }) => {
  const renderBody = generateBodyPreview(body, regexList)

  return (
    <NotificationContentContainer theme={theme}>
      <NotificationContentTypography theme={theme}>
        {title}
      </NotificationContentTypography>
      {body && <ReadMoreTypography renderBody={renderBody} theme={theme} />}
    </NotificationContentContainer>
  )
}

const ToastActionButton = ({ item, handleMarkAsRead }) => {
  const sameTab = item?.sameTab || 'false'
  const target = sameTab === 'false' ? '_blank' : '_self'
  const variant = item?.primary === 'true' ? 'contained' : 'outlined'

  const handleClick = (e) => {
    e.stopPropagation()
    if (handleMarkAsRead) {
      handleMarkAsRead(item)
    }

    if (sameTab === 'true') {
      window.open(item?.url, '_self')
    } else {
      window.open(item?.url, '_blank')
    }
  }

  return (
    <a
      target={target}
      href={`${item?.action}`}
      style={{ textDecoration: 'none' }}
      onClick={(e) => handleClick(e)}
      rel='noreferrer'
    >
      <Button variant={variant} size='small'>
        {item?.label}
      </Button>
    </a>
  )
}

const ToastNotificationActionButtons = ({ item, handleMarkAsRead }) => {
  const buttons = item?.notification_content?.buttons

  if (buttons?.length > 0) {
    return (
      <NotificationContentActionButtonsContainer>
        {buttons.map((buttonItem, index) => {
          return (
            <ToastActionButton
              key={buttonItem?.label + index}
              item={buttonItem}
              handleMarkAsRead={handleMarkAsRead}
            />
          )
        })}
      </NotificationContentActionButtonsContainer>
    )
  } else return null
}

export const ToastNotificationContent = ({ item, handleMarkAsRead, logo }) => {
  const theme = useTheme()

  const type = item?.notification_content?.attachments?.type
  let mainLink = item?.notification_content?.action?.href || null
  let sameTab = item?.notification_content?.action?.sameTab || false

  if (mainLink && mainLink[0] !== '/') {
    if (!(/^https:/.test(mainLink) || /^http:/.test(mainLink))) {
      if (/[a-zA-Z]\.[a-zA-Z]/.test(mainLink)) {
        mainLink = 'https://' + mainLink
      } else if (mainLink !== '#') {
        mainLink = '/' + mainLink
      } else {
        mainLink = null
        sameTab = 'true'
      }
    }
  } else {
    mainLink = null
    sameTab = 'true'
  }

  const title = item?.notification_content?.title
  const body = item?.notification_content?.body
  const notificationLogo = item?.notification_content?.icon || logo

  const attachmentsObject = item?.notification_content?.attachments
  const attachmentLink = attachmentsObject?.attachment

  const handleOpenNotificationInWindow = (e) => {
    e.stopPropagation()
    if (handleMarkAsRead) {
      handleMarkAsRead(item)
    }
    if (mainLink) window.open(mainLink, sameTab === 'true' ? '_self' : '_blank')
  }

  return (
    <InAppMessageContainer
      theme={theme}
      onClick={(e) => handleOpenNotificationInWindow(e)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleOpenNotificationInWindow(e)
        }
      }}
      role='button'
    >
      <CustomFlexBox alignItems='flex-start' gap='10px'>
        <NotificationIcon logo={notificationLogo} />
        <div style={{ width: '100%' }}>
          <ToastNotificationContentInner
            title={title}
            body={body}
            theme={theme}
          />
          <ToastNotificationActionButtons
            item={item}
            handleMarkAsRead={handleMarkAsRead}
          />
        </div>

        {attachmentsObject && (
          <CustomFlexBox
            flexDirection='column'
            alignItems='flex-end'
            justifyContent='space-between'
          >
            <div
              onClick={(e) => {
                e.stopPropagation()
                if (attachmentLink) {
                  window.open(attachmentLink, '_blank')
                }
              }}
              style={{ cursor: attachmentLink ? 'pointer' : 'default' }}
            >
              <NotificationMedia
                type={type}
                attachmentsObject={attachmentsObject}
              />
            </div>
          </CustomFlexBox>
        )}
      </CustomFlexBox>
    </InAppMessageContainer>
  )
}
